import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Row, Space, Spin, Table, Tabs, Tooltip, Typography } from 'antd';
import style from '../../VehicleCompatibility.module.scss';
import {
  getFeatureMeasurement,
  getSourceToolTip,
  getVehicleModelDataColumns
} from '../../VehicleModelHelper';
import {
  AuditOutlined,
  DownOutlined,
  EditOutlined,
  InfoCircleFilled,
  MergeCellsOutlined,
  RightOutlined,
  ScanOutlined,
  UpOutlined
} from '@ant-design/icons';
import { useCompanies } from '../../../../features/company/companyApi';
import { useDispatch, useSelector } from 'react-redux';
import { setPageNumber } from '../../../../features/vehicleModels/vehicleModelFilterSlice';
import PidLabel from '../PidLabel';
import { fetchDocument, useGetDocument } from '../../../../features/document/documentApi';
import { isValidHttpUrl } from '../../../../utils/strings';
import { Can } from '../../../../features/user/Can';
import { Permission } from '../../../../features/user/Permission';
import { ResponsiveButton } from '../../../../features/responsive';
import { RouteKey } from '../../../../config/page-key';
import { useTranslation } from 'react-i18next';
import { useCurrentCompany } from '../../../../features/company/companySlice';
import VehicleCompatibilityMergeModal from '../../VehicleCompatibilityMergeModal';
import VehicleCompatibilityInfoModal from '../../VehicleCompatibilityInfoModal';
import VehicleCompatibilityDetailModal from '../../VehicleCompatibilityDetailModal';
import { useUserKey } from '../../../../features/user/ssoSlice';
import { useHistory } from 'react-router-dom';
import useScanRequestState from '../../../../hooks/useScanRequestState';
import { getStatusConfig } from '../../constant';

const { Title, Text } = Typography;

const ClassicTable = ({ vehicleCompatibility, dataPoolIdentifiers }) => {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const history = useHistory();

  const companies = useCompanies();
  const currentCompany = useCurrentCompany();
  const userKey = useUserKey();
  const currentPage = useSelector(state => state.vehicleModelFilter.pageNumber);
  const { data: documents, isFetching: isFetchingDocument } = useGetDocument(currentCompany.id);

  const [selectedVehicleCompatibility, setSelectedVehicleCompatibility] = useState();
  const [infoModelOpen, setInfoModalOpen] = useState(false);
  const [vehicleMergeModal, setVehicleMergeModal] = useState(false);
  const [vehicleCompatibilityDetailModal, setVehicleCompatibilityDetailModal] = useState(false);
  const searchObj = useSelector(state => state.vehicleModelFilter.searchFilter);

  const { getScanRequestState, handleVote, handleUnvote } = useScanRequestState({ i18n, t });

  const documentMap = useMemo(() => {
    return Object.fromEntries(
      (documents || [])
        .filter(i => (i.data || []).length > 0)
        .map(item => [item.attachmentId, item])
    );
  }, [documents, isFetchingDocument]);

  const handlePageChange = page => {
    dispatch(setPageNumber(page));
  };

  const rowExpandRender = record => {
    const statusConfig = getStatusConfig();
    const tabItems = record.groupModel.sort((a, b) => (a.priority || 1) - (b.priority || 1)).map(model => {
      let pidTab = Object.keys(dataPoolIdentifiers).map(groupId => {
        return {
          key: groupId,
          label: (
            <Tooltip title={t('VehicleModel.MeasurementCategory.' + groupId + 'Tip')}>
              {t('VehicleModel.MeasurementCategory.' + groupId)}
            </Tooltip>
          ),
          children: (
            <>
              {(model.vins || []).length !== 0 ? model.vins.map(({ vinDetail, modelPids }) => {
                const vinKey = (vinDetail.vin || '').includes('/')
                  ? vinDetail.vin.split('/').pop()
                  : vinDetail.vin.split('_').pop();
                const pidListing = Object.entries(dataPoolIdentifiers[groupId])
                      .map(([key, value]) => ({ key, value }))
                      .filter(i=> (searchObj?.measurements || []).length === 0 || (searchObj?.measurements || []).includes(i.key))
                
                return (
                  <div key={vinKey}>
                    <div style={{ marginTop: 8, marginBottom: 10 }}>
                      <Text type={'secondary'} strong>
                        {t('VehicleModel.CanBus') +
                          ' ' +
                          (vinKey !== '' ? ' {' + vinKey + '}' : '')}
                      </Text>
                    </div>
                    <div className={style.pidLabelGroup}>
                      {pidListing.length > 0 ? pidListing.map(identifier => {
                          return (
                            <PidLabel
                              dataPoolIdentifier={identifier}
                              vin={vinDetail.vin}
                              modelPids={modelPids}
                              model={model}
                            />
                          );
                        }) : "-"}
                    </div>
                  </div>
                );
              }) : "-"}
            </>
          )
        };
      });

      pidTab = [...pidTab, 
        {
          "key":"feature",
          "label": t('VehicleModel.MeasurementCategory.feature'),
          "children": (
            <div>
              <div className={style.pidLabelGroup}>
                {
                  getFeatureMeasurement(model.supportedPid.filter(i=>i.verificationStatus !== "N" && i.verificationStatus !== "X" && (model.source?.toLowerCase() !== "internal" || i.verificationStatus !== "U")).map(i=>i.dataPoolIdentifier)).map(data => {
                    const featureStatus = data.supported ? statusConfig.VERIFIED : statusConfig.UNKNOWN;
                    const Icon = featureStatus.icon;
                    return (
                      <Tooltip title={data.supported ? t('VehicleModel.Supported') : t('VehicleModel.NotSupported')} placement="top">
                        <div className={style.pidLabel}>
                          <div className={style.iconWrapper}>
                            <Icon style={{ color: featureStatus.color, fontSize: '22px', lineHeight: '22px' }} />
                          </div>
                          <span>{t("VehicleModel.FeatureMeasurement." + data.key)}</span>
                        </div>
                      </Tooltip>
                    );
                  })}
              </div>
            </div>
          )
        }];

      const enrichedCanDatas = model.vins.map(({ vinDetail }) => {
        const canId = vinDetail.canId;
        let enrichedCan = { ...vinDetail };
        if (model.documentInfo?.installManualInfo) {
          enrichedCan = {
            ...enrichedCan,
            ...model.documentInfo?.installManualInfo[`can${canId}Input`]
          };
        }
        return enrichedCan;
      });

      const isCanCliqRequired = (model.extras?.connectionPoint || []).includes("CANClick");
      const scanState = getScanRequestState(model);

      return {
        key: model.id,
        label: (
          <Tooltip title={getSourceToolTip(model.source)}>
            {model.displaySource?.toUpperCase() + ' - ' + (model.priority ?? '1')}
          </Tooltip>
        ),
        children: (
          <Spin spinning={!!model.documentId && model.isDocumentFetched === undefined}>
            <div className={style.vehicleModelDetailCell}>
              <Row>
                <Col xs={{ span: 24 }}>
                  <Title level={5} style={{ fontSize: 18 }}>
                    {t('VehicleModel.InstallationData')}
                  </Title>
                </Col>
              </Row>
              <Row>
                <Col xs={{ span: 24 }} md={{ span: 12 }} xl={{ span: 5 }} style={{ padding: 10 }}>
                  <Space direction={'vertical'} size={8}>
                    <Text type="secondary" className={style.subHeader}>
                      {t('VehicleModel.SupportedDevices')}
                    </Text>
                    <Space direction={'horizontal'} size={8} wrap>
                      {(model.supportedModels || []).length !== 0
                        ? [...model.supportedModels].map((dm, index) => {
                            return (
                              <div key={index} className={style.smallCard}>
                                {dm.name}
                              </div>
                            );
                          })
                        : ' - '}
                    </Space>
                  </Space>
                </Col>
                <Col xs={{ span: 24 }} md={{ span: 12 }} xl={{ span: 6 }} style={{ padding: 10 }}>
                  <Space direction={'vertical'} size={15}>
                    <Text type="secondary" className={style.subHeader}>
                      {t('VehicleModel.ConnectionType')}
                    </Text>
                    <Row>
                      {(model.extras?.connectionPoint || []).length !== 0
                        ? <Space wrap size={[8, 12]}>
                          {
                            model.extras?.connectionPoint.map((d, index) => {
                              return (
                                <Col key={index} xs={{ span: 24 }}>
                                  <Text className={style.grayBg}>{d}</Text>
                                </Col>
                              );
                            })
                          }
                        </Space>
                        : ' - '}
                    </Row>
                  </Space>
                </Col>
                <Col xs={{ span: 24 }} md={{ span: 12 }} xl={{ span: 6 }} style={{ padding: 10 }}>
                  <Space direction={'vertical'} size={10}>
                    <Text type="secondary" className={style.subHeader}>
                      {t('VehicleModel.Notes')}
                    </Text>
                    {enrichedCanDatas.length > 1 && (
                      <Alert message={t('VehicleModel.CustomNote.OneCanAtATime')} type="info" />
                    )}
                    {isCanCliqRequired && (
                      <>
                        <Alert
                          type="warning"
                          message={t('VehicleModel.CustomNote.CanCliqRequired')}
                        />
                      </>
                    )}
                    {model.source === "inventure" && (
                      <>
                        {
                          model.supportedModels.find(i => i.name === "SI201") === undefined ? <Alert
                            message={t('VehicleModel.CustomNote.InstallationRecommended')}
                            type="info"
                          /> :
                            <Alert
                              message={t('VehicleModel.CustomNote.PlugAndPlaySupported')}
                              type="info"
                            />
                        }
                      </>
                    )}
                    {model?.extras?.note && <Alert message={model?.extras?.note} type="info" />}
                    {(model.attachments || []).length === 0 &&
                      !model?.extras?.note &&
                      !isCanCliqRequired &&<Text> - </Text>}
                  </Space>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 12 }}
                  xl={{ span: 6 }}
                  style={{ padding: 10, display: 'flex', flexDirection: 'column' }}
                >
                  <Text type="secondary" className={style.subHeader}>
                    {t('VehicleModel.Documentation')}
                  </Text>
                  <Space direction={'vertical'} size={0}>
                    {(model.attachments || []).length > 0 ? (
                      model.attachments.map((i, index) => {
                        const doc = documentMap[i.attachmentId];
                        return (
                          <Button
                            key={index}
                            style={{ padding: 0 }}
                            onClick={() => {
                              var win = window.open();
                              if (doc.mimeType === 'text/*') {
                                win.location = doc.url;
                              } else {
                                fetchDocument(i.documentId, dispatch, userKey)
                                  .then(e => {
                                    if (isValidHttpUrl(e)) {
                                      win.location = e;
                                    } else {
                                      win.document.write(e);
                                    }
                                  })
                                  .catch(e => win.close());
                              }
                            }}
                            type="link"
                            className={style.infoBtnIcon}
                            icon={
                              <InfoCircleFilled
                                onClick={e => {
                                  setInfoModalOpen(true);
                                  e.stopPropagation();
                                }}
                                className={style.infoIcon}
                              />
                            }
                          >
                            {doc?.name}
                          </Button>
                        );
                      })
                    ) : (
                      <Text> - </Text>
                    )}
                  </Space>
                </Col>
              </Row>
              <Row>
                <Col xs={{ span: 24 }}>
                  <Space direction="vertical">
                    <Title level={5} style={{ fontSize: 18 }}>
                      {t('VehicleModel.SupportedData')}
                    </Title>
                  </Space>
                </Col>
              </Row>

              <Tabs items={pidTab} />
            </div>
            <Row style={{ marginTop: 10 }}>
              <Col xs={{ span: 24 }} className={style.showMorePanel}>
                <div>
                  {scanState?.isEligibleForScanRequest && (
                    <ResponsiveButton
                      type="link"
                      disabled={scanState?.isUnvotable}
                      onClick={async () => {
                        if (scanState?.hasVoted) {
                          await handleUnvote(model.id);
                        } else {
                          await handleVote(model.id);
                        }
                      }}
                      title={
                        scanState?.isUnvotable
                          ? t('VehicleModel.PollClosed', {
                              status: t('VehicleModel.RequestStatus.' + scanState.unvotablePoll.status)
                            })
                          : scanState?.hasVoted
                          ? t('VehicleModel.CancelRequest')
                          : t('VehicleModel.RequestScan')
                      }
                      icon={<ScanOutlined />}
                    />
                  )}
                  <Can requiredPermission={[Permission.AUDIT]}>
                    <ResponsiveButton
                      type="link"
                      onClick={() => {
                        let records = (record.fullGroupModels || [])
                          .filter(i => i.source === model.source)
                          .map(i => i.id)
                          .filter((value, index, self) => self.indexOf(value) === index);

                        history.push(RouteKey.AUDIT_VEHICLE_MODEL, {
                          id: records,
                          name: `${model.make} ${model.model}`
                        });
                      }}
                      title={t('Audits.Audits')}
                      icon={<AuditOutlined />}
                    />
                  </Can>
                  <Can requiredPermission={[Permission.COMMON_EDIT]}>
                    <ResponsiveButton
                      type="link"
                      onClick={() => {
                        let records = (record.fullGroupModels || [])
                          .filter(i => i.source === model.source)
                          .map(i => i.id)
                          .filter((value, index, self) => self.indexOf(value) === index);
                        if (records.length > 1) {
                          history.push(RouteKey.VEHICLE_MODEL_GROUP, { record: records });
                        } else {
                          history.push(RouteKey.VEHICLE_MODEL, { record: model });
                        }
                      }}
                      title={t('Common.Edit')}
                      icon={<EditOutlined />}
                    />
                  </Can>
                  {(model.groupId === undefined || model.groupId === null) &&
                    !model.isDummyInternal && (
                      <Can requiredPermission={[Permission.MERGE_VEHICLE_MODEL]}>
                        <ResponsiveButton
                          type="link"
                          onClick={() => {
                            setSelectedVehicleCompatibility(model);
                            setVehicleMergeModal(true);
                          }}
                          title={t('VehicleModel.Merge')}
                          icon={<MergeCellsOutlined />}
                        />
                      </Can>
                    )}
                  <Button
                    type="link"
                    className={style.showMoreLink}
                    onClick={() => {
                      setSelectedVehicleCompatibility(record);
                      setVehicleCompatibilityDetailModal(true);
                    }}
                  >
                    {t('Common.ShowMore')}
                    <RightOutlined />
                  </Button>
                </div>
              </Col>
            </Row>
          </Spin>
        )
      };
    });
    return (
      <Row>
        <Col xs={{ span: 24 }} style={{ paddingLeft: 30 }}>
          <Tabs defaultActiveKey="1" items={tabItems} />
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Table
        rowKey={record => record.rowId}
        className={style.vehicleModelTable}
        columns={getVehicleModelDataColumns(companies)}
        dataSource={vehicleCompatibility}
        pagination={{
          current: currentPage,
          onChange: handlePageChange,
          showSizeChanger: true
        }}
        expandIcon={props => {
          if (props.expanded) {
            return (
              <a
                style={{ color: 'black' }}
                onClick={e => {
                  props.onExpand(props.record, e);
                }}
              >
                <UpOutlined />
              </a>
            );
          } else {
            return (
              <a
                style={{ color: 'black' }}
                onClick={e => {
                  props.onExpand(props.record, e);
                }}
              >
                <DownOutlined />
              </a>
            );
          }
        }}
        expandable={{
          expandedRowRender: record => rowExpandRender(record),
          rowExpandable: record => true
        }}
      />
      <VehicleCompatibilityInfoModal
        isModalOpen={infoModelOpen}
        onClose={() => {
          setInfoModalOpen(false);
        }}
      />
      <VehicleCompatibilityMergeModal
        isModalOpen={vehicleMergeModal}
        vehicleDetail={selectedVehicleCompatibility}
        onClose={() => {
          setSelectedVehicleCompatibility(undefined);
          setVehicleMergeModal(false);
        }}
      />
      <VehicleCompatibilityDetailModal
        vehicleDetail={selectedVehicleCompatibility}
        isModalOpen={vehicleCompatibilityDetailModal}
        onClose={() => {
          setSelectedVehicleCompatibility(undefined);
          setVehicleCompatibilityDetailModal(false);
        }}
      />
    </>
  );
};

export default ClassicTable;
